import { register } from "./registry";
import { toast } from "./toast";
import { moveFocus } from "./keyboard";
import { todayBs, fmt } from "./calendar";

register((root) => {
  const page = root.querySelector<HTMLElement>("[data-page='pod-register']");
  const dlg = page?.querySelector<HTMLDialogElement>("#kit-pod-modal");
  const form = dlg?.querySelector<HTMLFormElement>("#podMarkForm");
  if (!page || !dlg || !form) return;
  const idInput = form.querySelector<HTMLInputElement>("#podDeliveryItemId")!;
  const mitiInput = form.querySelector<HTMLInputElement>("#podMitiInput")!;
  const filesInput = form.querySelector<HTMLInputElement>("#podFilesInput")!;
  const confirmedInput = form.querySelector<HTMLInputElement>("#podSignedCopyConfirmed")!;
  const remarksInput = form.querySelector<HTMLInputElement>("#podRemarksInput")!;
  const saveBtn = form.querySelector<HTMLButtonElement>("#podSaveBtn")!;
  const error = form.querySelector<HTMLElement>("#podError")!;
  const progress = form.querySelector<HTMLElement>("#podProgress")!;
  const uploaded = new Map<File, string>();
  let busy = false;
  let confirming = false;
  let controller: AbortController | null = null;

  function close() {
    if (confirming) return;
    controller?.abort();
    dlg!.close();
  }
  form.querySelector("#podCancelBtn")?.addEventListener("click", close);
  dlg.addEventListener("cancel", (e) => { e.preventDefault(); close(); });
  page.querySelectorAll<HTMLButtonElement>("[data-pod-upload]").forEach((button) => {
    button.addEventListener("click", () => {
      form.reset();
      uploaded.clear();
      idInput.value = button.dataset.podUpload!;
      mitiInput.value = fmt(todayBs());
      error.textContent = "";
      progress.textContent = "";
      dlg.showModal();
      mitiInput.focus();
      mitiInput.select();
    });
  });
  filesInput.addEventListener("change", () => {
    // Changing the evidence requires a fresh attestation. Keep completed uploads for retry.
    confirmedInput.checked = false;
    error.textContent = "";
  });
  dlg.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      e.stopPropagation();
      void submitPod();
    } else if (e.key === "Enter" && e.target instanceof HTMLInputElement && e.target.type !== "file") {
      e.preventDefault();
      moveFocus(e.target, e.shiftKey ? -1 : 1);
    }
  });
  form.addEventListener("submit", (e) => { e.preventDefault(); void submitPod(); });

  async function responseError(response: Response, fallback: string): Promise<string> {
    try {
      const data = await response.json();
      return data.error?.message || data.error || data.message || data.title || fallback;
    } catch { return fallback; }
  }

  async function submitPod() {
    if (busy || !form!.reportValidity()) return;
    const files = Array.from(filesInput.files ?? []);
    if (files.length < 1 || files.length > 2) {
      error.textContent = "Choose one or two signed Bilti files.";
      filesInput.focus();
      return;
    }
    busy = true;
    saveBtn.disabled = true;
    filesInput.disabled = true;
    confirmedInput.disabled = true;
    controller = new AbortController();
    const signal = controller.signal;
    error.textContent = "";
    try {
      const fileIds: string[] = [];
      for (const [index, file] of files.entries()) {
        progress.textContent = `Uploading file ${index + 1} of ${files.length}… CC Receipt remains No until confirmed.`;
        let id = uploaded.get(file);
        if (!id) {
          const data = new FormData();
          data.append("file", file);
          const response = await fetch("/api/v1/files", { method: "POST", body: data, signal });
          if (!response.ok) throw new Error(await responseError(response, "File upload failed. Try again."));
          const saved = await response.json();
          if (typeof saved.id !== "string") throw new Error("File upload did not complete. Try again.");
          id = saved.id as string;
          uploaded.set(file, id);
        }
        fileIds.push(id);
      }
      if (signal.aborted) return;
      confirming = true;
      progress.textContent = "Confirming signed proof…";
      const token = form!.querySelector<HTMLInputElement>("input[name='__RequestVerificationToken']")?.value || "";
      const response = await fetch("/Operations/Pod?handler=Mark", {
        method: "POST", signal,
        headers: { "Content-Type": "application/json", "RequestVerificationToken": token },
        body: JSON.stringify({ deliveryItemId: idInput.value, miti: mitiInput.value,
          remarks: remarksInput.value, fileIds, signedCopyConfirmed: confirmedInput.checked })
      });
      if (!response.ok) throw new Error(await responseError(response, "Could not confirm signed proof. Try again."));
      toast("ok", "Signed proof confirmed");
      dlg!.close();
      window.location.reload();
    } catch (failure) {
      if (!signal.aborted) {
        error.textContent = failure instanceof Error ? failure.message : "Could not confirm signed proof. Try again.";
        progress.textContent = "Proof was not confirmed. Review the files and try again.";
      }
    } finally {
      busy = false;
      confirming = false;
      controller = null;
      saveBtn.disabled = false;
      filesInput.disabled = false;
      confirmedInput.disabled = false;
    }
  }
});
